import { LocalStorageKeys } from '@/constants/enums/LocalStorageEnum';
import type { StorageSexValue, StorageUserStore } from '@/models/interface/storageValue';
import { RoleType } from '@/models/interface/user';
import { UtilCommon } from '@/utils/utilCommon';
import { jwtDecode } from 'jwt-decode';
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';

interface jwtDecodeType {
  exp: number;
  iss: string;
  member_id: string;
  nbf: string;
}

function customEncodeURIComponent(str: string) {
  // 使用標準的encodeURIComponent函數進行編碼
  return (
    encodeURIComponent(str)
      // 將編碼後的十六進位數字轉換為小寫
      .replace(/%[0-9A-F]{2}/g, (matched) => matched.toLowerCase())
  );
}

function initUserLoginStatus() {
  const user = useUserStore();
  user.needGetMemberInfo = true;
  user.isLogin = false;
  user.token = '';
  localStorage.removeItem('questionnaireIsShowed');
}

export default async function auth({
  to,
  from,
  next
}: {
  to: RouteLocationNormalized;
  from: RouteLocationNormalized;
  next: NavigationGuardNext;
}) {
  const user = useUserStore();
  const customizedLoginPage = [
    '/login',
    '/loginJunior',
    '/loginMusicTube',
    '/loginReading',
    '/lineBinding',
    '/zingala',
    '/buynpay',
    '/social_FB_landing',
    '/branding'
  ];

  if (customizedLoginPage.includes(to.path)) {
    if (user.token === '' || user.token === null || user.token === undefined) {
      initUserLoginStatus();
      return next();
    }

    // 判斷是否需要取得用戶資料
    if (user.needGetMemberInfo === true) {
      const memberId = jwtDecode<jwtDecodeType>(user.token).member_id;
      usePopStore().loadingStatus = true;
      user.userData = await userApi.getMemberInfo(memberId);
      user.isPayUser = await userApi.isPayUser();
      user.remainingTickets = await userApi.remainingTickets();
      if (!user.isPayUser) {
        user.phoneExist = await userApi.getPhoneExist();
        if (!user.phoneExist) {
          if (user.isAfterThirdPartVerify) {
            useAppStore().isShowNewSMSVerifyPopup = true;
          } else {
            initUserLoginStatus();
            window.location.href = '/login';
            return next();
          }
        }
        user.isWithdrawCoupon = await userApi.isWithdraw();
      }
      user.isLogin = true;
      user.needGetMemberInfo = false;
      user.customerId = user.userData.customerId;
      user.aiTeacherAssistant = user.userData.chatIds.aiTeacherAssistant;
      await UtilCommon.sleep(500);
      usePopStore().loadingStatus = false;
      return next();
    }

    return next();
  } else {
    // 之後需要完全移除fdtk邏輯
    if (to?.query?.fdtk) {
      // 驗證fdtk是否有效
      const fdtk = customEncodeURIComponent(to.query.fdtk as string);
      if (fdtk) {
        const res = await userApi.userLoginFdtk(fdtk);
        if (res.content) {
          const data = res.content;
          const memberId = jwtDecode<jwtDecodeType>(data).member_id;
          user.token = data;
          usePopStore().loadingStatus = true;
          user.userData = await userApi.getMemberInfo(memberId);
          user.isPayUser = await userApi.isPayUser();
          user.remainingTickets = await userApi.remainingTickets();
          if (!user.isPayUser) {
            user.phoneExist = await userApi.getPhoneExist();
            if (!user.phoneExist) {
              if (user.isAfterThirdPartVerify) {
                useAppStore().isShowNewSMSVerifyPopup = true;
              } else {
                initUserLoginStatus();
                window.location.href = '/login';
                return next();
              }
            }
            user.isWithdrawCoupon = await userApi.isWithdraw();
          }
          console.log('user.userData', user.userData);
          user.isLogin = true;
          user.customerId = user.userData.customerId;
          user.aiTeacherAssistant = user.userData.chatIds.aiTeacherAssistant;
          user.needGetMemberInfo = false;
          await UtilCommon.sleep(500);
          usePopStore().loadingStatus = false;
          return next();
        }
      }
    } else {
      // 原始邏輯
      // useCheckAdIdParam();
      if (user.isLogin === true) {
        const loginPage =
          user.userData.roleType === RoleType['isJunior'] ? '/loginJunior' : '/login';

        // 檢查token是否過期
        if (user.token === '' || user.token === null || user.token === undefined) {
          initUserLoginStatus();
          window.location.href = loginPage;
          return next();
        }

        const expireToken = useExpireToken(user.token);
        if (expireToken) {
          alert(user.forceLogoutMsg);
          initUserLoginStatus();
          window.location.href = loginPage;
          return next();
        } else {
          if (user.needGetMemberInfo === true) {
            const memberId = jwtDecode<jwtDecodeType>(user.token).member_id;
            usePopStore().loadingStatus = true;
            user.userData = await userApi.getMemberInfo(memberId);
            user.isPayUser = await userApi.isPayUser();
            user.remainingTickets = await userApi.remainingTickets();
            if (!user.isPayUser) {
              user.phoneExist = await userApi.getPhoneExist();
              if (!user.phoneExist) {
                if (user.isAfterThirdPartVerify) {
                  useAppStore().isShowNewSMSVerifyPopup = true;
                } else {
                  initUserLoginStatus();
                  window.location.href = '/login';
                  return next();
                }
              }
              user.isWithdrawCoupon = await userApi.isWithdraw();
            }
            user.isLogin = true;
            user.customerId = user.userData.customerId;
            user.aiTeacherAssistant = user.userData.chatIds.aiTeacherAssistant;
            user.needGetMemberInfo = false;

            const storageUser = UtilCommon.getLocalStorage<StorageUserStore>(
              LocalStorageKeys.UserStore
            );
            const storage = UtilCommon.getLocalStorage<StorageSexValue[]>('sex');
            const newStorage: StorageSexValue[] = [
              {
                userAccount: storageUser?.userAccount ?? '',
                value: user.userData.sex
              }
            ];
            if (storage) {
              newStorage.push(
                ...storage.filter((item) => item.userAccount !== (storageUser?.userAccount ?? ''))
              );
            }
            UtilCommon.setLocalStorage<StorageSexValue[]>('sex', newStorage);
            await UtilCommon.sleep(500);
            usePopStore().loadingStatus = false;
            return next();
          } else {
            console.log(6);
            return next();
          }
        }
      } else {
        usePopStore().loadingStatus = true;
        console.log('Query ad_id');
        initUserLoginStatus();

        // find ad_id value in window.location.search
        // await useCheckAdIdParam();
        window.location.href = '/login';
        usePopStore().loadingStatus = false;
        return next();
      }
    }
  }
}
